import { useAtom } from '@reatom/npm-react'
import { Navigate, useMatch } from 'react-router'

import { isLoggedInAtom } from '@root/entities/auth/atoms'
import {
  AuthRoute,
  RootRoute,
  TradeRoute,
  TradeV2Route,
} from '@root/features/routing'
import { isTauriOrBrowserLocalAtom, PUBLIC_MARKET_PAGES } from '@root/shared'

import { terminalAccessGrantedAtom } from '../atoms/reatom-terminal-access'
import { chooseDoor } from '../model/choose-door'

import { DoorScreen } from './door-screen'
import { WaitlistScreen } from './waitlist-screen'

import type { ChildrenProps } from '@root/shared/react'

/**
 * The terminal's front door.
 *
 * Nobody signed in is sent to sign in, or — on the web, which has no sign-in
 * of its own yet — told where the door is. Somebody signed in and not let in
 * yet sees the queue. Only an account the server says is in sees the
 * terminal. The server enforces the same rule on every route; this keeps the
 * screen honest about it.
 *
 * Except on the market pages, where a build may open the door: the feed and
 * a token. Those two are matched here rather than by lifting them out of the
 * router, because the token page is a child of the feed's route and so are
 * the wallet, profile and order pages that must stay shut — the paths are
 * the only thing that tells them apart.
 */
export const TerminalAccessGate: React.FC<ChildrenProps> = ({ children }) => {
  const [isLoggedIn] = useAtom(isLoggedInAtom)
  const [isApp] = useAtom(isTauriOrBrowserLocalAtom)
  const [granted] = useAtom(terminalAccessGrantedAtom)

  // Three unconditional matches: hooks cannot be skipped, and the flag is
  // folded in afterwards so a production build short-circuits to false.
  const onFeed = useMatch({ path: RootRoute.path, end: true })
  const onPair = useMatch(TradeRoute.path)
  const onPairV2 = useMatch(TradeV2Route.path)
  const isPublicPage =
    PUBLIC_MARKET_PAGES && Boolean(onFeed ?? onPair ?? onPairV2)

  switch (chooseDoor({ isLoggedIn, isApp, granted, isPublicPage })) {
    // Signing in is the desktop app's screen; on a website it renders
    // nothing and navigates straight back here, and the two would push the
    // router at each other until React gives up. The web door is a screen,
    // not a redirect.
    case 'sign-in': {
      return <Navigate to={AuthRoute.build()} replace />
    }
    case 'door': {
      return <DoorScreen />
    }
    // Nothing, rather than a flash of the app or of the queue.
    case 'unknown': {
      return undefined
    }
    case 'queue': {
      return <WaitlistScreen />
    }
    case 'terminal': {
      return children
    }
  }
}
